import { createContext, useCallback, useContext, useMemo, useState } from "react";

import type { ReactNode } from "react";

import { useHistory } from "react-router-dom";

import { clearAuth, getAuth, hasAuth, saveAuth } from "services/authStore";

import type { AuthData } from "types";

// Auth changes twice in a session: on sign in and on sign out. Keeping it in its own
// context means nothing else in the app re-renders when a toast or a badge refresh
// happens, and vice versa.
type AuthValue = {
  isAuthorized: boolean;
  authData: AuthData;
  signIn: (credentials: AuthData) => void;
  signOut: () => void;
};

const AuthContext = createContext<AuthValue | null>(null);

AuthContext.displayName = "AuthContext";

function AuthProvider({ children }: { children: ReactNode }) {
  const history = useHistory();

  // Read synchronously instead of in an effect: there is no "unknown" first paint to
  // guard against any more, so the app no longer flashes a placeholder on every load.
  const [authData, setAuthDataState] = useState<AuthData>(getAuth);
  const [isAuthorized, setIsAuthorized] = useState(hasAuth);

  // The module store has to move together with the state, otherwise a request fired
  // from a child mount effect would still be using the previous credentials.
  const signIn = useCallback((credentials: AuthData) => {
    setAuthDataState(saveAuth(credentials));
    setIsAuthorized(true);
  }, []);

  const signOut = useCallback(() => {
    setAuthDataState(clearAuth());
    setIsAuthorized(false);
    history.push("/sign-in");
  }, [history]);

  const value = useMemo(
    () => ({
      isAuthorized: isAuthorized,
      authData: authData,
      signIn: signIn,
      signOut: signOut,
    }),
    [isAuthorized, authData, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth(): AuthValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AppProvider.");
  }

  return context;
}

export { AuthProvider, useAuth };
